// ScheduleManager.jsx
import React, { useState } from 'react';
import { Plus, Clock, Trash, X, Save } from 'lucide-react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Checkbox, 
  ListItemText, 
  OutlinedInput,
  Chip,
  Box 
} from '@mui/material';
import './styles/scheduleManager.scss';

const ScheduleManager = ({ schedule = [], onChange }) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeInput, setTimeInput] = useState('');
  const [times, setTimes] = useState([]);

  const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

  const resetForm = () => {
    setSelectedDays([]);
    setTimeInput('');
    setTimes([]);
    setIsAddingNew(false);
    setEditingIndex(null);
  };

  const handleAddTime = () => {
    if (!timeInput) return;
    
    // Evitar duplicados
    if (!times.includes(timeInput)) {
      setTimes([...times, timeInput]);
      setTimeInput('');
    } else {
      alert('Este horario ya ha sido agregado');
    }
  };

  const handleRemoveTime = (timeToRemove) => {
    setTimes(times.filter(time => time !== timeToRemove));
  };

  const handleSaveSchedule = () => {
    if (selectedDays.length === 0) {
      alert('Debes seleccionar al menos un día');
      return;
    }

    if (times.length === 0) {
      alert('Debes agregar al menos un horario');
      return;
    }

    // Crear nuevas entradas para cada día seleccionado
    const newScheduleItems = selectedDays.map(dayNumber => ({
      dayOfWeek: dayNumber,
      times: [...times]
    }));

    // Filtrar el schedule actual para eliminar días que vamos a actualizar
    const filteredSchedule = schedule.filter(day => !selectedDays.includes(day.dayOfWeek));

    // Combinar con los nuevos días
    const updatedSchedule = [...filteredSchedule, ...newScheduleItems];
    
    // Ordenar por día de la semana
    updatedSchedule.sort((a, b) => a.dayOfWeek - b.dayOfWeek);
    
    onChange(updatedSchedule);
    resetForm();
  };

  const handleEditDay = (day) => {
    setSelectedDays([day.dayOfWeek]);
    setTimes(day.times || []);
    setIsAddingNew(true);
    setEditingIndex(schedule.findIndex(d => d.dayOfWeek === day.dayOfWeek));
  };

  const handleDeleteDay = (dayOfWeek) => {
    if (window.confirm(`¿Estás seguro de eliminar los horarios para ${dayNames[dayOfWeek]}?`)) {
      const updatedSchedule = schedule.filter(day => day.dayOfWeek !== dayOfWeek);
      onChange(updatedSchedule);
    }
  };

  // Función para manejar el cambio en el selector múltiple
  const handleDayChange = (event) => {
    const { value } = event.target;
    setSelectedDays(value);
  };

  return (
    <div className="schedule-manager">
      <div className="manager-header">
        <h3>Horarios</h3>
        {!isAddingNew && (
          <button
            onClick={() => setIsAddingNew(true)}
            className="btn-add"
          >
            <Plus size={16} className="icon" />
            AÑADIR HORARIO
          </button>
        )}
      </div>

      {isAddingNew && (
        <div className="form-container">
          <div className="form-header">
            <h4>{editingIndex !== null ? 'Editar Horario' : 'Nuevo Horario'}</h4>
            <button 
              onClick={resetForm}
              className="btn-close"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="form-content">
            <div className="form-group">
              <FormControl fullWidth>
                <InputLabel id="multiple-day-label">Días</InputLabel>
                <Select
                  labelId="multiple-day-label"
                  id="multiple-day-select"
                  multiple
                  value={selectedDays}
                  onChange={handleDayChange}
                  input={<OutlinedInput label="Días" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={dayNames[value]} />
                      ))}
                    </Box>
                  )}
                >
                  {dayNames.map((name, index) => (
                    <MenuItem key={index} value={index}>
                      <Checkbox checked={selectedDays.indexOf(index) > -1} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            
            <div className="form-group">
              <label>Agregar Horas</label>
              <div className="time-input-container">
                <input
                  type="time"
                  value={timeInput}
                  onChange={(e) => setTimeInput(e.target.value)}
                />
                <button
                  onClick={handleAddTime}
                  className="btn-add-time"
                >
                  Agregar
                </button>
              </div>
              
              <div className="time-list">
                {times.length === 0 ? (
                  <p className="no-times">No hay horarios agregados</p>
                ) : (
                  <div className="times-container">
                    {times.map((time, index) => (
                      <div key={index} className="time-badge">
                        <Clock size={14} className="icon" />
                        <span>{time}</span>
                        <button
                          onClick={() => handleRemoveTime(time)}
                          className="btn-remove-time"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <button
              onClick={resetForm}
              className="btn-cancel"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveSchedule}
              className="btn-save"
              disabled={selectedDays.length === 0 || times.length === 0}
            >
              <Save size={16} className="icon" />
              {editingIndex !== null ? 'Actualizar Horario' : 'Guardar Horario'}
            </button>
          </div>
        </div>
      )}

      {schedule.length === 0 && !isAddingNew ? (
        <div className="empty-state">
          <Clock size={32} className="empty-icon" />
          <p className="empty-text">No hay horarios definidos</p>
          <p className="empty-subtext">
            Añade horarios para establecer cuándo se deben realizar las rondas
          </p>
        </div>
      ) : (
        <div className="schedule-list">
          {schedule.map((day) => (
            <div key={day.dayOfWeek} className="day-item">
              <div className="day-content">
                <div className="day-info">
                  <div className="day-name">{dayNames[day.dayOfWeek]}</div>
                  <div className="time-badges">
                    {day.times?.map((time, index) => (
                      <div key={index} className="time-badge">
                        <Clock size={14} className="icon" />
                        {time}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="day-actions">
                  <button
                    onClick={() => handleEditDay(day)}
                    className="btn-edit"
                    title="Editar"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteDay(day.dayOfWeek)}
                    className="btn-delete"
                    title="Eliminar"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScheduleManager;